import { useEffect, useRef, useState } from 'preact/hooks'
import { Image as ImageIcon, Import, Loader2, TriangleAlert, X } from 'lucide-preact'
import type {
  BrandAssetBackground,
  BrandTokenRole,
  DesignSystemImportLogoChoice,
  DesignSystemImportLogoDto,
  DesignSystemImportPreviewDto,
  DesignSystemImportRoleChoice,
  DesignSystemImportRoleDto,
  KnowledgeBaseFileViewDto,
} from '@/types/knowledgeBase'
import { FileUpload } from '@/components/common/FileUpload'
import { SegmentedToggle } from '@/components/common/SegmentedToggle'
import { Select } from '@/components/common/Select'
import { BRAND_CLASSES } from '@/constants/theme'
import { fileKind, inWords } from '@/components/console/knowledgeBase/brandGuidelinesModel'
import {
  ROLE_LABELS, matchedRoles, otherOptions, questionsHeading, questionsLede, roleChoices, roleQuestions, rolesWaitLine,
  rolesWithAnswers, sampleOf, unansweredRoles, type RoleAnswers,
} from '@/components/console/knowledgeBase/designSystemImportModel'
import { useZipImages, type ZipImages } from '@/components/console/knowledgeBase/useZipImages'
import { formatBytes } from '@/utils/fileSize'
import { RoleSwatches } from '../BrandGuidelinesRow'
import { DARK_GROUND } from './BrandLogoRows'
import { MARK_SLOTS, slotPhrase, type MarkType } from './brandLogos'
import { CONFIRM_BUTTON, EYEBROW, GHOST_SMALL, SWATCH_FRAME } from './brandGuidelinesStyles'
import {
  changeGroups, clashingSlots, logoChoices, previewGround, replacedFile, suggestedChoice, type LogoChoice,
} from './designSystemImport'
import type { ImportPreviewOutcome, WriteOutcome } from './useBrandGuidelines'

interface Props {
  /** Whose brand the import replaces, by name: the company's, or one workspace's. */
  ownerName: string
  /** The files this level's brand has now, which a chosen logo replaces in its slot. None while a workspace uses the company's. */
  currentFiles: readonly KnowledgeBaseFileViewDto[]
  pending: boolean
  onPreview: (zip: File) => Promise<ImportPreviewOutcome>
  /** Null when another write is still running. */
  onImport: (zip: File, logos: DesignSystemImportLogoChoice[], roles: DesignSystemImportRoleChoice[]) => Promise<WriteOutcome | null>
  onClose: () => void
}

type Reading =
  | { kind: 'none' }
  | { kind: 'reading'; zip: File }
  | { kind: 'refused'; message: string }
  | { kind: 'ready'; zip: File; preview: DesignSystemImportPreviewDto }

const SLOT_OPTIONS = [...MARK_SLOTS.map(slot => ({ value: slot.type, label: slot.label })), { value: 'None', label: 'Not a logo' }]

/**
 * White: the ground a logo found for light backgrounds, or either, is shown on, and the card a shadow is drawn on. A logo
 * for dark backgrounds sits on the dark ground Brand Guidelines shows dark versions on.
 */
const LIGHT_GROUND = '#FFFFFF'

/**
 * Importing a design system made in Claude (ORB-1028), in a slide-over from Brand Guidelines. The zip is read and its
 * changes shown first, with nothing saved; a role its own names do not settle is asked, with nothing chosen, and each
 * logo in it starts where the API guesses it goes, and a person checks each one before importing. An imported brand
 * counts as confirmed, and Populate never overwrites it.
 */
export function DesignSystemImportPanel({ ownerName, currentFiles, pending, onPreview, onImport, onClose }: Props) {
  const [zip, setZip] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | undefined>(undefined)
  const [reading, setReading] = useState<Reading>({ kind: 'none' })
  const [choices, setChoices] = useState<Record<string, LogoChoice>>({})
  const [answers, setAnswers] = useState<RoleAnswers>({})
  const [importError, setImportError] = useState<string | null>(null)
  // Bumped by every file chosen, so an older file's preview never lands over a newer one's.
  const latest = useRef(0)

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape' && !pending) onClose() }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [onClose, pending])

  const read = async (file: File | null) => {
    const asked = ++latest.current
    setZip(file)
    setAnswers({})
    setImportError(null)
    if (file === null) {
      setReading({ kind: 'none' })
      return
    }
    setFileError(undefined)
    setReading({ kind: 'reading', zip: file })
    const outcome = await onPreview(file)
    if (asked !== latest.current) return
    if (!outcome.ok) {
      setReading({ kind: 'refused', message: outcome.message })
      return
    }
    setChoices(Object.fromEntries(outcome.preview.Logos.map(logo => [logo.Path, suggestedChoice(logo)])))
    setReading({ kind: 'ready', zip: file, preview: outcome.preview })
  }

  const ready = reading.kind === 'ready' ? reading : null
  const clashes = ready ? clashingSlots(ready.preview.Logos, choices) : []
  const unanswered = ready ? unansweredRoles(ready.preview.Roles, answers) : []
  const waitForRoles = rolesWaitLine(unanswered)
  const blocked = clashes.length > 0 || unanswered.length > 0

  const confirmImport = async () => {
    if (!ready || blocked) return
    setImportError(null)
    const outcome = await onImport(ready.zip, logoChoices(ready.preview.Logos, choices), roleChoices(ready.preview.Roles, answers))
    if (outcome === null) return
    if (outcome.ok) onClose()
    else setImportError(outcome.message)
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" onClick={pending ? undefined : onClose} />
      <div role="dialog" aria-modal="true" aria-labelledby="ds-import-title"
        className="absolute inset-y-0 right-0 w-full max-w-3xl h-full flex flex-col bg-white dark:bg-slate-900 shadow-xl">
        <div className="shrink-0 px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 shrink-0 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center">
              <Import className="w-4 h-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 id="ds-import-title" className="text-lg font-semibold text-slate-900 dark:text-white">Import a design system</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Into {ownerName}'s Brand Guidelines. Nothing is saved until you import.</p>
            </div>
          </div>
          <button type="button" aria-label="Close" title="Close" disabled={pending} onClick={onClose}
            className="shrink-0 p-2 rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50">
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-8">
          <div className="flex flex-col gap-3">
            <p className="max-w-[68ch] text-sm text-slate-600 dark:text-slate-300">
              Download the design system from Claude as a zip, then choose it here. Its colors, type, spacing, corner sizes and
              shadows replace {ownerName}'s, and you choose where each logo in it goes.
            </p>
            <FileUpload
              label="Design system"
              accept=".zip,application/zip,application/x-zip-compressed"
              maxSizeMB={50}
              selectedFile={zip}
              onFileChange={file => void read(file)}
              onError={setFileError}
              error={fileError}
              helperText="Choose the design system's zip file"
            />
          </div>

          {reading.kind === 'reading' && (
            <p role="status" className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />Reading {reading.zip.name}...
            </p>
          )}
          {reading.kind === 'refused' && (
            <p role="alert" className={`flex items-start gap-2 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>
              <TriangleAlert className="w-4 h-4 flex-none mt-0.5" aria-hidden="true" />{reading.message}
            </p>
          )}
          {ready && (
            <Preview zip={ready.zip} preview={ready.preview} ownerName={ownerName} currentFiles={currentFiles}
              choices={choices} onChoose={(path, choice) => setChoices(current => ({ ...current, [path]: choice }))}
              answers={answers} onAnswer={(role, name) => setAnswers(current => ({ ...current, [role]: name }))} />
          )}
        </div>

        <div className="shrink-0 px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex flex-col gap-2">
          {waitForRoles && <p role="alert" className="text-sm text-amber-800 dark:text-amber-300">{waitForRoles}</p>}
          {clashes.length > 0 && (
            <p role="alert" className="text-sm text-amber-800 dark:text-amber-300">
              {clashes.length === 1
                ? `More than one file is chosen for ${clashes[0]}, which holds one. Keep one of them there.`
                : `More than one file is chosen for ${inWords(clashes)}, which hold one each. Keep one in each.`}
            </p>
          )}
          {importError && <p role="alert" className={`text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>{importError}</p>}
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button type="button" className={GHOST_SMALL} disabled={pending} onClick={onClose}>Cancel</button>
            <button type="button" className={CONFIRM_BUTTON} disabled={!ready || pending || blocked} onClick={() => void confirmImport()}>
              {pending && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {pending ? 'Importing...' : 'Import and confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Preview({ zip, preview, ownerName, currentFiles, choices, onChoose, answers, onAnswer }: {
  zip: File
  preview: DesignSystemImportPreviewDto
  ownerName: string
  currentFiles: readonly KnowledgeBaseFileViewDto[]
  choices: Record<string, LogoChoice>
  onChoose: (path: string, choice: LogoChoice) => void
  answers: RoleAnswers
  onAnswer: (role: BrandTokenRole, name: string) => void
}) {
  const groups = changeGroups(preview.Changes)
  const images = useZipImages(zip, preview.Logos)
  return (
    <>
      <section aria-labelledby="ds-summary-h" className="-mx-6 px-6 py-5 flex flex-col gap-2 bg-slate-50 dark:bg-slate-800 border-y border-slate-200 dark:border-slate-700">
        <h3 id="ds-summary-h" className="text-sm font-semibold text-slate-900 dark:text-white">
          Importing {preview.SystemName} into {ownerName}
        </h3>
        <p className="max-w-[68ch] text-sm text-slate-600 dark:text-slate-300">
          An imported brand counts as confirmed. Emails, landing pages and asset QA use it straight away, and Populate does not
          overwrite it.
        </p>
        <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <span>Email colors after the import</span>
          <RoleSwatches roles={rolesWithAnswers(preview.Incoming.Roles, preview.Roles, answers)} owner={preview.SystemName} />
        </p>
      </section>

      <RoleQuestions preview={preview} answers={answers} onAnswer={onAnswer} />

      <section aria-labelledby="ds-changes-h" className="flex flex-col gap-3">
        <h3 id="ds-changes-h" className={EYEBROW}>Changes</h3>
        {groups.length === 0 ? (
          <p className="text-sm text-slate-600 dark:text-slate-300">Nothing changes: the design system matches {ownerName}'s brand.</p>
        ) : groups.map(group => (
          <div key={group.family} className="flex flex-col gap-1.5">
            <p className="text-sm font-medium text-slate-900 dark:text-white">{group.family}: {group.counts}</p>
            <ul aria-label={group.family} className="flex flex-col divide-y divide-slate-200 dark:divide-slate-700 border-y border-slate-200 dark:border-slate-700">
              {group.changes.map(change => (
                <li key={`${change.Family}:${change.Name}`} className="py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className="min-w-0 flex-1 font-mono text-[13px] text-slate-900 dark:text-white break-words">{change.Name}</span>
                  <span className="font-mono text-[13px] text-slate-600 dark:text-slate-300 break-all">
                    {change.Kind === 'Added' ? change.After : change.Kind === 'Removed' ? change.Before : `${change.Before} → ${change.After}`}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${KIND_PILL[change.Kind]}`}>{change.Kind}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      <section aria-labelledby="ds-logos-h" className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 id="ds-logos-h" className={EYEBROW}>Logos found</h3>
          {preview.Logos.length > 0 && (
            <p className="max-w-[68ch] text-sm text-slate-600 dark:text-slate-300">
              Each starts where the design system suggests it goes. Check each one: importing confirms what is chosen here, and a
              chosen file replaces the one in its place now.
            </p>
          )}
        </div>
        {preview.Logos.length === 0 ? (
          <p className="text-sm text-slate-600 dark:text-slate-300">No logo files were found in the design system, so the logos stay as they are.</p>
        ) : (
          <ul aria-label="Logos found" className="flex flex-col divide-y divide-slate-200 dark:divide-slate-700 border-y border-slate-200 dark:border-slate-700">
            {preview.Logos.map((logo, index) => (
              <LogoRow key={logo.Path} logo={logo} index={index} images={images} choice={choices[logo.Path] ?? suggestedChoice(logo)}
                replaced={replacedFile(currentFiles, choices[logo.Path] ?? suggestedChoice(logo))}
                onChoose={choice => onChoose(logo.Path, choice)} />
            ))}
          </ul>
        )}
      </section>

      {preview.Dropped.length > 0 && (
        <section aria-labelledby="ds-dropped-h" className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h3 id="ds-dropped-h" className={EYEBROW}>Left out, and why</h3>
            <p className="max-w-[68ch] text-sm text-slate-600 dark:text-slate-300">
              These are in {preview.SystemName} but have no place in Brand Guidelines, so the import skips them.
            </p>
          </div>
          <ul aria-label="Left out" className="flex flex-col divide-y divide-slate-200 dark:divide-slate-700 border-y border-slate-200 dark:border-slate-700">
            {preview.Dropped.map(dropped => (
              <li key={dropped.Name} className="py-2.5 flex flex-col gap-0.5 text-sm">
                <span className="font-mono text-[13px] font-medium text-slate-900 dark:text-white break-words">{dropped.Name}</span>
                <span className="text-slate-600 dark:text-slate-300">{dropped.Reason}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  )
}

const KIND_PILL: Record<'Added' | 'Changed' | 'Removed', string> = {
  Added: BRAND_CLASSES.STATUS.SUCCESS,
  Changed: BRAND_CLASSES.STATUS.INFO,
  Removed: BRAND_CLASSES.STATUS.DEFAULT,
}

/**
 * The roles the design system's own names do not settle, each asked with the likeliest of its tokens as cards and the rest
 * in a list under them. Nothing starts chosen. Below them, the roles its names did settle.
 */
function RoleQuestions({ preview, answers, onAnswer }: {
  preview: DesignSystemImportPreviewDto
  answers: RoleAnswers
  onAnswer: (role: BrandTokenRole, name: string) => void
}) {
  const questions = roleQuestions(preview.Roles)
  if (questions.length === 0) return null
  const matched = matchedRoles(preview.Roles)
  return (
    <section aria-labelledby="ds-roles-h" className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h3 id="ds-roles-h" className="text-sm font-semibold text-slate-900 dark:text-white">{questionsHeading(questions.length)}</h3>
        <p className="max-w-[68ch] text-sm text-slate-600 dark:text-slate-300">{questionsLede(preview.SystemName, questions.length)}</p>
      </div>
      <ul aria-label="Roles to choose" className="flex flex-col divide-y divide-slate-200 dark:divide-slate-700 border-y border-slate-200 dark:border-slate-700">
        {questions.map(question => (
          <RoleQuestion key={question.Role} question={question} chosen={answers[question.Role] ?? null}
            onChoose={name => onAnswer(question.Role, name)} />
        ))}
      </ul>
      {matched.length > 0 && (
        <div className="flex flex-col gap-2">
          <p id="ds-matched-h" className="text-xs font-medium text-slate-500 dark:text-slate-400">Taken from the design system's own names</p>
          <ul aria-labelledby="ds-matched-h" className="flex flex-wrap gap-2">
            {matched.map(({ role, option }) => (
              <li key={role} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                {sampleOf(role) === 'color' && (
                  <span aria-hidden="true" className="w-3 h-3 rounded-sm ring-1 ring-inset ring-slate-900/10 dark:ring-slate-50/40" style={{ background: option.Value }} />
                )}
                <span>{ROLE_LABELS[role].label} · <span className="font-mono">{option.Name}</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

/** One role's question. Real radios, so the cards are one radio group to a screen reader and arrow keys move between them. */
function RoleQuestion({ question, chosen, onChoose }: {
  question: DesignSystemImportRoleDto
  chosen: string | null
  onChoose: (name: string) => void
}) {
  const { label, purpose } = ROLE_LABELS[question.Role]
  const id = `ds-role-${question.Role}`
  const others = otherOptions(question)
  const otherChosen = others.find(option => option.Name === chosen)?.Name ?? ''
  const placeholder = question.Suggested.length > 0 ? 'Another token' : 'Choose a token'
  return (
    <li className="py-4 grid grid-cols-1 sm:grid-cols-[150px_minmax(0,1fr)] gap-x-6 gap-y-3">
      <div className="flex flex-col gap-0.5">
        <p id={id} className="text-sm font-medium text-slate-900 dark:text-white">{label}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400">{purpose}</p>
      </div>
      <div className="min-w-0 flex flex-col gap-3">
        {question.Suggested.length > 0 && (
          <div role="radiogroup" aria-labelledby={id} className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-2.5">
            {question.Suggested.map(option => {
              const on = chosen === option.Name
              return (
                <label key={option.Name}
                  className={`relative min-w-0 flex flex-col gap-1 p-2.5 rounded-lg cursor-pointer bg-white dark:bg-slate-800 ring-inset transition-colors duration-200 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-primary-500 ${
                    on ? 'ring-2 ring-primary-600 dark:ring-primary-400' : 'ring-1 ring-slate-300 dark:ring-slate-600 hover:ring-slate-400 dark:hover:ring-slate-500'}`}>
                  <input type="radio" className="sr-only" name={id} value={option.Name} checked={on} onChange={() => onChoose(option.Name)} />
                  <OptionSample role={question.Role} value={option.Value} />
                  <span className="font-mono text-xs font-semibold text-slate-900 dark:text-white break-all">{option.Name}</span>
                  {sampleOf(question.Role) !== 'type' && <span className="font-mono text-xs text-slate-500 dark:text-slate-400 break-all">{option.Value}</span>}
                </label>
              )
            })}
          </div>
        )}
        {others.length > 0 && (
          <Select name={`${id}-other`} label={`${placeholder} for ${label}`} labelClassName="sr-only" density="compact"
            containerClassName="w-full max-w-xs" placeholder={placeholder} value={otherChosen}
            className={otherChosen ? 'ring-2 ring-primary-600 dark:ring-primary-400' : ''}
            options={others.map(option => ({ value: option.Name, label: `${option.Name} · ${option.Value}` }))}
            onChange={(event: Event) => {
              const value = (event.target as HTMLSelectElement).value
              if (value) onChoose(value)
            }} />
        )}
      </div>
    </li>
  )
}

/**
 * An option drawn as what it is: a swatch, a corner, a raised card, or a type style in words. The brand's own color and
 * shadow sit on a fixed ground, so they show the same in either app theme.
 */
function OptionSample({ role, value }: { role: BrandTokenRole; value: string }) {
  const sample = sampleOf(role)
  if (sample === 'color') return <span aria-hidden="true" className={`block h-10 ${SWATCH_FRAME}`} style={{ background: value }} />
  if (sample === 'radius') {
    return (
      <span aria-hidden="true" className="h-10 grid place-items-center rounded-md bg-slate-50 dark:bg-slate-900">
        <span className="w-7 h-7 bg-slate-100 dark:bg-slate-700 ring-[1.5px] ring-inset ring-slate-300 dark:ring-slate-500" style={{ borderRadius: value }} />
      </span>
    )
  }
  if (sample === 'shadow') {
    return (
      <span aria-hidden="true" className="h-10 grid place-items-center rounded-md" style={{ background: LIGHT_GROUND }}>
        <span className="w-11 h-6 rounded-[5px]" style={{ background: LIGHT_GROUND, boxShadow: value }} />
      </span>
    )
  }
  return <span className="text-sm font-medium text-slate-900 dark:text-white break-words">{value}</span>
}

function LogoRow({ logo, index, images, choice, replaced, onChoose }: {
  logo: DesignSystemImportLogoDto
  index: number
  images: ZipImages
  choice: LogoChoice
  replaced: KnowledgeBaseFileViewDto | null
  onChoose: (choice: LogoChoice) => void
}) {
  const slot = MARK_SLOTS.find(candidate => candidate.type === choice.type) ?? null
  const destination = slot === null
    ? 'Left out of the brand.'
    : `Becomes ${slotPhrase(slot, 'the', choice.background)}${replaced ? `, in place of ${replaced.FileName}` : ''}.`
  const url = images.kind === 'read' ? images.urls[logo.Path] ?? null : null
  const ground = previewGround(choice)
  return (
    <li className="py-4 grid grid-cols-[72px_minmax(0,1fr)] gap-x-4 gap-y-3">
      {url ? (
        <span aria-hidden="true" data-ground={ground} style={{ background: ground === 'Dark' ? DARK_GROUND : LIGHT_GROUND }}
          className={`w-18 h-18 p-2 grid place-items-center overflow-hidden ${SWATCH_FRAME}`}>
          <img src={url} alt="" className="max-w-full max-h-full object-contain" />
        </span>
      ) : (
        <span aria-hidden="true" className="w-18 h-18 grid place-items-center rounded-lg bg-slate-50 dark:bg-slate-800 ring-1 ring-inset ring-slate-200 dark:ring-slate-700 text-slate-500 dark:text-slate-400">
          <ImageIcon className="w-5 h-5" />
        </span>
      )}
      <div className="min-w-0 flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <p className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-mono text-[13px] font-semibold text-slate-900 dark:text-white break-all">{logo.FileName}</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">{fileKind(logo.ContentType, logo.FileName)} · {formatBytes(logo.SizeBytes)}</span>
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-300">{destination}</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <Select name={`ds-logo-${index}`} label={`Use ${logo.FileName} as`} labelClassName="sr-only" density="compact"
            containerClassName="w-44" options={SLOT_OPTIONS} value={choice.type ?? 'None'}
            onChange={(event: Event) => {
              const value = (event.target as HTMLSelectElement).value
              onChoose(value === 'None' ? { type: null, background: null } : { type: value as MarkType, background: choice.background })
            }} />
          {choice.type !== null && (
            <SegmentedToggle<'Either' | BrandAssetBackground>
              ariaLabel={`Which backgrounds ${logo.FileName} is for`}
              value={choice.background ?? 'Either'}
              onChange={value => onChoose({ type: choice.type, background: value === 'Either' ? null : value })}
              options={[
                { value: 'Either', label: 'Either background' },
                { value: 'Light', label: 'Light backgrounds' },
                { value: 'Dark', label: 'Dark backgrounds' },
              ]}
            />
          )}
        </div>
      </div>
    </li>
  )
}
